import React from 'react';

import { FormField, Heading, Label, ToggleButton, ToggleButtonGroup } from '@zillow/constellation-10';

import type { Section } from '../../../../../models';
import YesAndNoExpansion from '../../../../Widgets/YesAndNoExpansion';
import CurrencyInput from '../../../../Widgets/CurrencyInput';

type Props = {
  section: Section;
  utilityOwnRentalPropertyYesField: string;
  utilityOwnRentalPropertyNoField: string;
  annualCostsField: string;
  knowAnnualCostsSimilarRentalUnitField: string;
  setSectionValues: (keyValuePairs: object) => void;
  utilityName: string;
};

export default function UtilitiesOwnRentalPropertyAnnualCosts({
  section,
  setSectionValues,
  utilityName,
  utilityOwnRentalPropertyYesField,
  utilityOwnRentalPropertyNoField,
  annualCostsField,
  knowAnnualCostsSimilarRentalUnitField,
}: Props): JSX.Element {
  function onNoOwnRentalProperty(): void {
    setSectionValues({
      [utilityOwnRentalPropertyYesField]: false,
      [utilityOwnRentalPropertyNoField]: true,
    });
  }

  function onYesOwnRentalProperty(): void {
    setSectionValues({
      [utilityOwnRentalPropertyYesField]: true,
      [utilityOwnRentalPropertyNoField]: false,
    });
  }

  function onNoKnowAnnualCostsSimilarRentalUnitField(): void {
    setSectionValues({
      [knowAnnualCostsSimilarRentalUnitField]: false,
    });
  }

  function onYesKnowAnnualCostsSimilarRentalUnitField(): void {
    setSectionValues({
      [knowAnnualCostsSimilarRentalUnitField]: true,
    });
  }

  function onAnnualCostsChange(event): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [annualCostsField]: value,
    });
  }

  const yesFieldValue = section.getFieldValue(utilityOwnRentalPropertyYesField);
  const noFieldValue = section.getFieldValue(utilityOwnRentalPropertyNoField);

  return (
    <>
      <Heading level={3} textStyle="responsive.heading-xs" css={{ marginBottom: 'layout.tight' }}>
        {utilityName}
      </Heading>
      <FormField
        required
        label={
          <Label>{`Did you own the rental property during the previous 12 months and pay the ${utilityName.toLowerCase()} costs to the utility provider on behalf of the tenant?`}</Label>
        }
        control={
          <ToggleButtonGroup
            conjoined
            aria-label="tenant parking"
            css={{ marginBottom: 'layout.tight', width: '100%' }}
          >
            <ToggleButton onClick={onYesOwnRentalProperty} selected={yesFieldValue}>
              Yes
            </ToggleButton>
            <ToggleButton onClick={onNoOwnRentalProperty} selected={noFieldValue}>
              No
            </ToggleButton>
          </ToggleButtonGroup>
        }
      />
      {yesFieldValue && (
        <FormField
          required
          label={
            <Label>
              What are the annual costs of service from the utility provider that provided the primary service during
              the previous 12 months?
            </Label>
          }
          control={<CurrencyInput value={section.getFieldValue(annualCostsField)} onChange={onAnnualCostsChange} />}
        />
      )}
      {noFieldValue && (
        <FormField
          required
          label={<Label>Do you know the annual costs of service for a similar rental unit?</Label>}
          control={
            <YesAndNoExpansion
              yesActive={section.getFieldValue(knowAnnualCostsSimilarRentalUnitField)}
              yesHandler={onYesKnowAnnualCostsSimilarRentalUnitField}
              noHandler={onNoKnowAnnualCostsSimilarRentalUnitField}
            >
              <FormField
                required
                label={<Label>What are the annual costs?</Label>}
                control={
                  <CurrencyInput value={section.getFieldValue(annualCostsField)} onChange={onAnnualCostsChange} />
                }
              />
            </YesAndNoExpansion>
          }
        />
      )}
    </>
  );
}
